// UAT 2026-05-13 — shared per-project invoice breakdown renderer used by both
// the preview surface (`InvoiceGeneratePage`) and the detail page
// (`InvoiceDetailPage`). Petra: the breakdown must look identical on every
// surface — single component is the only way to guarantee future drift can't
// introduce a hourly-rate display on a Hour Package invoice or vice versa.
//
// Per-project `kind` discriminates between two billing modes:
//   - 'hourly': entries x billingRate; renders a User/Hours/Rate/Total table
//   - 'monthly_package' (SLA Hour Package): monthly fee x CNB FX rate to CZK;
//     renders a 4-column metadata row (monthly fee in source currency, FX
//     rate, package hours, total in CZK).
//
// Quick task 260902-eg2 (FX-DISPLAY) — all FOUR branch renderers (balíček,
// Fixed Price, Time & Material, hourly/Default) now share one `FxRateCell`
// so the FX line reads identically everywhere. T&M and hourly rows also
// render a source-amount / FX-rate / CZK-total grid (D-01 fallback chain)
// when `sourceCurrency` is present, or a mixed-currency note (D-04) when
// `mixedSourceCurrencies` is present instead.

import { Link } from 'react-router-dom';
import type {
  InvoiceBreakdown,
  InvoiceOverageMonth,
  InvoiceOverageRoleRow,
  InvoiceUserBreakdown,
} from '../../services/api/invoices';
import { formatCurrency } from '../../utils/format';
// MKS-552 — "October 2026" for a per-month overage heading, from the shared util.
import { DATE_PLACEHOLDER, formatMonthYear } from '../../utils/dateFormat';
import { LEGACY_EN_NUMBER_LOCALE, numberFormatter } from '../../utils/localeFormat';

// Quick task 260902-eg2 (D-01, D-07) — shared FX rate label + "as of" cell.
// Lifted verbatim from the pre-refactor Fixed Price FX block so styling and
// wording cannot drift between branches. Three states:
//   - currency CZK or empty → the existing em-dash-plus-CZK placeholder
//   - non-CZK with a numeric rate → "1 CUR = R.RRR CZK" (toFixed(3))
//   - non-CZK with no rate → a rate-unavailable placeholder (D-07 — this
//     replaces the old `project.fxRate ?? 1` lie on the balíček excluded-
//     package edge; it is the one deliberate visual change in this task).
// The "as of" sub-line renders only for non-CZK rows that actually have a date.
function FxRateCell({
  currency,
  fxRate,
  fxRateDate,
}: {
  currency: string;
  fxRate: number | undefined;
  fxRateDate: string | undefined;
}) {
  const isNonCzk = Boolean(currency) && currency !== 'CZK';
  const fxDate = fxRateDate ?? '';

  return (
    <div>
      <div className="text-xs text-gray-500 uppercase tracking-wider mb-0.5">CNB FX rate</div>
      <div className="font-medium text-gray-900 tabular-nums">
        {!isNonCzk ? (
          '— (CZK)'
        ) : fxRate != null ? (
          `1 ${currency} = ${fxRate.toFixed(3)} CZK`
        ) : (
          '— (rate unavailable)'
        )}
      </div>
      {isNonCzk && fxDate && (
        <div className="text-[11px] text-gray-500 mt-0.5">as of {fxDate}</div>
      )}
    </div>
  );
}

// Quick task 260902-eg2 (D-01, D-04) — shared source-amount / FX-rate / CZK-
// total metadata block for the T&M and hourly/Default rows. Renders nothing
// when neither `sourceCurrency` nor `mixedSourceCurrencies` is present, so a
// CZK-native row stays pixel-identical to today (D-03). Uses the same grid
// classes the Fixed Price row already uses for its metadata grid.
function SourceCurrencyMetadata({
  project,
}: {
  project: InvoiceBreakdown['byProject'][number];
}) {
  if (project.mixedSourceCurrencies) {
    return (
      <div className="px-6 py-2">
        <p className="text-xs text-gray-500 italic">
          Mixed source currencies ({project.mixedSourceCurrencies.join(', ')}) — no single FX
          rate applies.
        </p>
      </div>
    );
  }

  if (!project.sourceCurrency) return null;

  const sourceAmountNum = project.sourceAmount ?? 0;
  const formattedSourceAmount = numberFormatter({
    style: 'currency',
    currency: project.sourceCurrency,
    maximumFractionDigits: 2,
  }, LEGACY_EN_NUMBER_LOCALE).format(sourceAmountNum);

  return (
    <div className="px-6 py-3 grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
      <div>
        <div className="text-xs text-gray-500 uppercase tracking-wider mb-0.5">
          Billed amount (source currency)
        </div>
        <div className="font-medium text-gray-900">{formattedSourceAmount}</div>
        <div className="text-[11px] text-gray-500 mt-0.5">Pre-conversion figure</div>
      </div>
      <FxRateCell
        currency={project.sourceCurrency}
        fxRate={project.fxRate}
        fxRateDate={project.fxRateDate}
      />
      <div className="text-right">
        <div className="text-xs text-gray-500 uppercase tracking-wider mb-0.5">Billed (CZK)</div>
        <div className="font-semibold text-gray-900 tabular-nums">
          {formatCurrency(project.totalBilling)}
        </div>
        <div className="text-[11px] text-gray-500 mt-0.5">Contributes to invoice total</div>
      </div>
    </div>
  );
}

// Quick task 260912-j8z (G-01) — the expenses line, mounted above the
// discount line in every branch row (reading order: hours, then expenses,
// then discount). Pure function of `project.expenses`, same authoring style
// as `DiscountLine`/`FxRateCell` — returns null when there is nothing to
// show. Renders ONE row per receipt, never an aggregate: producing a sum
// here would mean adding money on the client, which this task never does.
// `expenseSheetId` is optional/nullable: a snapshot generated before this
// task, or a receipt whose expense sheet resolved to nothing, renders the
// same label as plain text rather than a broken link.
function ExpenseLine({
  project,
}: {
  project: InvoiceBreakdown['byProject'][number];
}) {
  const expenses = project.expenses;
  if (!expenses || expenses.length === 0) return null;

  return (
    <div className="border-t border-gray-200 bg-gray-50/60 px-6 py-2">
      <div className="text-sm font-medium text-gray-900 mb-1">Expenses</div>
      <div className="space-y-0.5">
        {expenses.map((receipt) => {
          const label =
            receipt.description ?? receipt.receiptDate ?? `Receipt #${receipt.receiptId}`;
          return (
            <div key={receipt.receiptId} className="flex justify-between items-center gap-2">
              <span className="text-xs text-gray-700">
                {typeof receipt.expenseSheetId === 'number' ? (
                  <Link
                    to={`/expenses/${receipt.expenseSheetId}`}
                    className="hover:underline"
                  >
                    {label}
                  </Link>
                ) : (
                  label
                )}
              </span>
              <span className="text-xs text-gray-700 tabular-nums">
                {formatCurrency(receipt.billedAmount)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Phase 110 (Plan 110-09, D-07, D-12) — the discount line, mounted below the
// financial content of every branch row. Pure function of `project.discount`
// and the row's own already-rendered figures — returns null when there is
// nothing to show, same authoring style as `FxRateCell`/`SourceCurrencyMetadata`.
// The backend omits a zero-valued block in every case EXCEPT an explicit
// generation-time override (110-11, closing 110-REVIEW CR-03): there, the
// block is emitted ON PURPOSE so the waiver is legible on the invoice. On
// that block `percentage` is 0, `amountCzk` is 0, and the `overridden` line
// below already renders it as `Override: 0% (configured: N%)`.
//
// Every figure comes from `project.discount` and is rendered with the shared
// `formatCurrency` — never derived from `totalBilling`/`entry.totalBilling`/
// any timesheet aggregate. On project 770 the entries sum to 431,750 against
// an invoice of 416,638.75, a normal but undocumented gap; a base derived
// from entries would make correct discount arithmetic look wrong.
function DiscountLine({
  project,
}: {
  project: InvoiceBreakdown['byProject'][number];
}) {
  const discount = project.discount;
  if (!discount) return null;

  // Copywriting contract (110-UI-SPEC.md) base label variants: `overage` when
  // the retainer is excluded from the base on a balíček project, `all billed
  // hours` when it is included (or on hourly/T&M, which have no retainer at
  // all), with `manual` appended in manual mode.
  const baseLabel =
    discount.mode === 'manual'
      ? 'manual'
      : discount.retainerInBase
        ? 'all billed hours'
        : 'overage';

  const formattedAmount = `-${formatCurrency(discount.amountCzk)}`;

  let baseLine: string;
  if (discount.baseCurrency === 'MIXED' || discount.baseSourceAmount == null) {
    baseLine = `${formatCurrency(discount.baseAmountCzk)} · ${formattedAmount}`;
  } else if (discount.baseCurrency === 'CZK') {
    baseLine = `${formatCurrency(discount.baseSourceAmount)} · ${formattedAmount}`;
  } else {
    const formattedSource = numberFormatter({
      style: 'currency',
      currency: discount.baseCurrency,
      maximumFractionDigits: 2,
    }, LEGACY_EN_NUMBER_LOCALE).format(discount.baseSourceAmount);
    baseLine = `${formattedSource} (${formatCurrency(discount.baseAmountCzk)}) · ${formattedAmount}`;
  }

  return (
    <div className="border-t border-rose-100 bg-rose-50/40 px-6 py-2">
      <div className="flex justify-between items-center gap-2">
        <span className="text-sm font-medium text-rose-900">
          Discount ({discount.percentage}% of {baseLabel})
        </span>
        {discount.overridden && (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-[10.5px] font-medium bg-rose-200 text-rose-900">
            Override: {discount.percentage}% (configured: {discount.configuredPercentage != null ? `${discount.configuredPercentage}%` : 'none'})
          </span>
        )}
      </div>
      <div className="text-xs text-rose-800 tabular-nums mt-0.5">{baseLine}</div>
    </div>
  );
}

// Phase 109 (D109-04) — the operator's only diagnosis surface: generateInvoice
// REFUSES when any hourly row has hasZeroRate === true (worst-bucket = a
// no_rate user), and the refusal surfaces on the Generate button where a long
// list of names does not fit. This banner derives its own data from the
// breakdown already in props — no new prop, no new fetch, no new hook — and
// names every blocking project and person so the operator never has to leave
// the page. Renders nothing for the all-priced case, the all-explicit_zero
// case, and every historical snapshot (none of them have a no_rate user).
function UnpricedHoursBanner({ breakdown }: { breakdown: InvoiceBreakdown }) {
  const affected = breakdown.byProject
    .filter((p) => p.kind === 'hourly' && p.hasZeroRate === true)
    .map((p) => ({
      projectName: p.projectName,
      userNames: (p.users ?? [])
        .filter((u) => u.rateCoverage === 'no_rate')
        .map((u) => u.userName),
    }))
    .filter((p) => p.userNames.length > 0);

  if (affected.length === 0) return null;

  return (
    <div className="mx-6 mt-4 rounded-lg border border-rose-200 bg-rose-50 p-4">
      <p className="text-sm font-semibold text-rose-900">
        Invoice cannot be generated — unpriced hours
      </p>
      <p className="mt-1 text-sm text-rose-800">
        These hours have no billing rate on their project assignment. Generation is blocked
        until a rate is set, or set to 0 if the work is deliberately non-billable.
      </p>
      <ul className="mt-2 space-y-0.5 text-sm text-rose-800">
        {affected.map((p) => (
          <li key={p.projectName}>
            {p.projectName} &mdash; {p.userNames.join(', ')}
          </li>
        ))}
      </ul>
    </div>
  );
}

// Phase 109 (D109-04, D109-06) — the four-case rate cell for an hourly user
// row. Never uses a falsy test on rateCoverage: an absent field (historical
// snapshot) matches neither strict comparison and falls through to the
// unchanged formatCurrency(billingRate) case.
function HourlyRateCell({ user }: { user: InvoiceUserBreakdown }) {
  if (user.rateCoverage === 'no_rate') {
    return <span className="text-gray-400">&mdash;</span>;
  }

  if (user.rateCoverage === 'explicit_zero') {
    return <>{formatCurrency(0)}</>;
  }

  if (user.sourceCurrency) {
    const formattedSourceRate = numberFormatter({
      style: 'currency',
      currency: user.sourceCurrency,
      maximumFractionDigits: 2,
    }, LEGACY_EN_NUMBER_LOCALE).format(user.sourceRate ?? 0);
    return (
      <>
        <div>
          {formattedSourceRate}
          {user.mixedRates === true && <span className="ml-1 text-gray-500">(mixed)</span>}
        </div>
        <div className="text-[11px] text-gray-500">{formatCurrency(user.billingRate)}</div>
      </>
    );
  }

  return (
    <>
      {formatCurrency(user.billingRate)}
      {user.mixedRates === true && <span className="ml-1 text-gray-500">(mixed)</span>}
    </>
  );
}

export interface InvoiceBreakdownTableProps {
  breakdown: InvoiceBreakdown;
  /**
   * Title displayed in the header. Default 'Invoice Preview' suits the
   * generate page; `InvoiceDetailPage` overrides to 'Breakdown'.
   */
  title?: string;
}

export function InvoiceBreakdownTable({
  breakdown,
  title = 'Invoice Preview',
}: InvoiceBreakdownTableProps) {
  if (breakdown.byProject.length === 0) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <p className="text-yellow-800 text-sm">
          No uninvoiced entries (hourly) and no SLA Hour Package projects to
          invoice for the selected projects and date range.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
        <h3 className="text-sm font-medium text-gray-900">{title}</h3>
      </div>

      <UnpricedHoursBanner breakdown={breakdown} />

      {breakdown.byProject.map((project) =>
        project.kind === 'monthly_package' ? (
          <PackageProjectRow key={project.projectId} project={project} />
        ) : project.kind === 'fixed_price' ? (
          <FixedPriceProjectRow key={project.projectId} project={project} />
        ) : project.kind === 'fixed_price_excluded' ? (
          <FixedPriceExcludedRow key={project.projectId} project={project} />
        ) : project.kind === 'time_and_material' ? (
          <TimeAndMaterialProjectRow key={project.projectId} project={project} />
        ) : (
          <HourlyProjectRow key={project.projectId} project={project} />
        ),
      )}

      <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
        {breakdown.discountTotal ? (
          // Phase 110 (Plan 110-09, D-10, D-11) — three stacked lines only
          // when a discount exists anywhere in the invoice. Sourced from
          // `breakdown.grossTotal`/`discountTotal`/`totalBilling` — the same
          // values Plan 110-06 persists to the two `Invoice` columns — never
          // summed client-side, so the footer can never disagree with the
          // stored figures. The net line keeps the EXACT className the
          // single `Total` row above uses, so it inherits the same visual
          // weight the old total had (D-11 makes net the authoritative
          // figure).
          <div className="space-y-1">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Gross (before discount)</span>
              <span className="text-sm text-gray-600">
                {formatCurrency(breakdown.grossTotal ?? breakdown.totalBilling)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Discount</span>
              <span className="text-sm text-gray-600">
                -{formatCurrency(breakdown.discountTotal)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm font-semibold text-gray-900">
                Net (after discount) — invoiced
              </span>
              <span className="text-sm font-semibold text-gray-900">
                {breakdown.totalHours.toFixed(1)}h / {formatCurrency(breakdown.totalBilling)}
              </span>
            </div>
          </div>
        ) : (
          <div className="flex justify-between items-center">
            <span className="text-sm font-semibold text-gray-900">Total</span>
            <span className="text-sm font-semibold text-gray-900">
              {breakdown.totalHours.toFixed(1)}h / {formatCurrency(breakdown.totalBilling)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

function HourlyProjectRow({
  project,
}: {
  project: InvoiceBreakdown['byProject'][number];
}) {
  const users = project.users ?? [];
  return (
    <div className="border-b border-gray-100 last:border-b-0">
      <div className="px-6 py-3 bg-gray-50/50">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-900">
              <Link to={`/projects/${project.projectId}`} className="hover:underline">
                {project.projectName}
              </Link>
            </span>
            <span className="inline-flex items-center px-2 py-0.5 rounded text-[10.5px] font-medium bg-indigo-100 text-indigo-800">
              Hourly
            </span>
            {project.hasZeroRate && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-[10.5px] font-medium bg-rose-200 text-rose-900">
                Zero-rate row
              </span>
            )}
          </div>
          <span className="text-sm text-gray-600">
            {project.totalHours.toFixed(1)}h / {formatCurrency(project.totalBilling)}
          </span>
        </div>
      </div>
      <SourceCurrencyMetadata project={project} />
      <table className="min-w-full">
        <thead>
          <tr className="text-xs text-gray-500 uppercase">
            <th className="px-6 py-2 text-left">User</th>
            <th className="px-6 py-2 text-right">Hours</th>
            <th className="px-6 py-2 text-right">Rate</th>
            <th className="px-6 py-2 text-right">Total</th>
          </tr>
        </thead>
        <tbody>
          {users.map((user) => (
            <tr key={user.userId} className="border-t border-gray-50">
              <td className="px-6 py-2 text-sm text-gray-900">
                {user.userName}
                {user.rateCoverage === 'no_rate' && (
                  <span className="ml-2 inline-flex items-center rounded bg-rose-100 px-1.5 py-0.5 text-[10px] font-semibold text-rose-800">
                    Rate not set
                  </span>
                )}
                {user.rateCoverage === 'explicit_zero' && (
                  <span className="ml-2 inline-flex items-center rounded bg-gray-200 px-1.5 py-0.5 text-[10px] font-semibold text-gray-800">
                    Non-billable
                  </span>
                )}
              </td>
              <td className="px-6 py-2 text-sm text-gray-600 text-right">{user.hours.toFixed(1)}</td>
              <td className="px-6 py-2 text-sm text-gray-600 text-right">
                <HourlyRateCell user={user} />
              </td>
              <td className="px-6 py-2 text-sm text-gray-900 text-right font-medium">{formatCurrency(user.totalBilling)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <ExpenseLine project={project} />
      <DiscountLine project={project} />
    </div>
  );
}

function PackageProjectRow({
  project,
}: {
  project: InvoiceBreakdown['byProject'][number];
}) {
  const paushal = project.paushalAmount ?? 0;
  const currency = project.paushalCurrency ?? '';
  // D-07 — no `?? 1` default: a non-CZK balíček row with no resolved rate
  // must not claim a 1.000 rate. FxRateCell renders "— (rate unavailable)".
  const fxRate = project.fxRate;
  const formattedPaushal = currency
    ? numberFormatter({
        style: 'currency',
        currency,
        maximumFractionDigits: 2,
      }, LEGACY_EN_NUMBER_LOCALE).format(paushal)
    : paushal.toFixed(2);

  return (
    <div className="border-b border-gray-100 last:border-b-0">
      <div className="px-6 py-3 bg-blue-50/40">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-900">
              <Link to={`/projects/${project.projectId}`} className="hover:underline">
                {project.projectName}
              </Link>
            </span>
            <span className="inline-flex items-center px-2 py-0.5 rounded text-[10.5px] font-medium bg-blue-100 text-blue-800">
              SLA Hour Package
            </span>
          </div>
          <span className="text-sm text-gray-600">
            Issued {formattedPaushal} / Booked {formatCurrency(project.totalBilling)}
          </span>
        </div>
      </div>
      <div className="px-6 py-3 grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
        <div>
          <div className="text-xs text-gray-500 uppercase tracking-wider mb-0.5">
            Monthly fee (issued)
          </div>
          <div className="font-medium text-gray-900">{formattedPaushal}</div>
          <div className="text-[11px] text-gray-500 mt-0.5">
            Invoice billed to customer
          </div>
        </div>
        <FxRateCell currency={currency} fxRate={fxRate} fxRateDate={project.fxRateDate} />
        <div>
          <div className="text-xs text-gray-500 uppercase tracking-wider mb-0.5">
            Package hours
          </div>
          <div className="font-medium text-gray-900 tabular-nums">
            {project.packageHours != null ? `${project.packageHours} h / month` : '—'}
          </div>
        </div>
      </div>
      {project.overage && (
        <OverageSection overage={project.overage} projectId={project.projectId} />
      )}
      <ExpenseLine project={project} />
      <DiscountLine project={project} />
      {/* Petra UAT 2026-07-14 — Booked total (CZK, internal) moved to the final
          row (was in the top fee grid): monthly fee + hours first, overage in
          the middle, the internal CZK sum last. */}
      <div className="px-6 py-3 flex justify-between items-baseline border-t border-gray-100">
        <span className="text-xs text-gray-500 uppercase tracking-wider">
          Booked total (CZK) · for internal statistics
        </span>
        <span className="text-sm font-semibold text-gray-900 tabular-nums">
          {formatCurrency(project.totalBilling)}
        </span>
      </div>
    </div>
  );
}

// MKS-552 — "Not included" copy per reason. An earlier month earns its place
// on this invoice by being approved in full with every role rated; anything
// else is shown, greyed, and left for a later invoice.
const NOT_INCLUDED_LABEL: Record<
  NonNullable<InvoiceOverageMonth['notIncludedReason']>,
  string
> = {
  awaiting_approval: 'Not included — awaiting approval',
  partial_approval: 'Not included — approved for fewer hours',
  missing_rate: 'Not included — role rate not set',
};

/** The role table of one overage block. Identical markup in both layouts. */
function OverageRoleTable({ byRole }: { byRole: InvoiceOverageRoleRow[] }) {
  return (
    <table className="min-w-full">
      <thead>
        <tr className="text-xs text-gray-500 uppercase">
          <th className="px-2 py-2 text-left">Role</th>
          <th className="px-2 py-2 text-right">Hours</th>
          <th className="px-2 py-2 text-right">Rate</th>
          <th className="px-2 py-2 text-right">Billing (CZK)</th>
        </tr>
      </thead>
      <tbody>
        {byRole.map((row) => (
          <tr key={`${row.roleId ?? 'none'}`} className="border-t border-amber-100/60">
            <td className="px-2 py-1.5 text-sm text-gray-900">
              {row.roleName ?? <span className="italic text-gray-500">(no role)</span>}
              {!row.hasRate && (
                <span className="ml-2 inline-flex items-center rounded bg-rose-100 px-1.5 py-0.5 text-[10px] font-semibold text-rose-800">
                  Rate not set
                </span>
              )}
            </td>
            <td className="px-2 py-1.5 text-sm text-gray-700 text-right tabular-nums">
              {row.hours.toFixed(1)}
            </td>
            <td className="px-2 py-1.5 text-sm text-gray-700 text-right tabular-nums">
              {row.hasRate ? formatCurrency(row.hourlyRate) : <span className="text-gray-400">&mdash;</span>}
            </td>
            <td className="px-2 py-1.5 text-sm text-gray-900 text-right font-medium tabular-nums">
              {formatCurrency(row.billing)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** The approval chip of one month — the same three states the single-block
 *  layout has always shown, keyed on that month's own approval. */
function OverageApprovalChip({
  approval,
  hours,
}: {
  approval: InvoiceOverageMonth['approval'];
  hours: number;
}) {
  if (!approval) return null;
  if (approval.status === 'less') {
    return (
      <span
        className="inline-flex items-center px-2 py-0.5 rounded text-[10.5px] font-medium bg-amber-100 text-amber-900"
        title={`PM approved ${approval.approvedHours?.toFixed(1) ?? '0'} h, actual overage is ${hours.toFixed(1)} h.`}
      >
        PM approved {approval.approvedHours?.toFixed(1) ?? '0'} h &lt; actual {hours.toFixed(1)} h
      </span>
    );
  }
  if (approval.status === 'missing') {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-[10.5px] font-medium bg-gray-200 text-gray-800">
        No PM approval
      </span>
    );
  }
  return null;
}

/** MKS-552 — one month's block in the per-month layout. */
function OverageMonthBlock({
  month,
  projectId,
}: {
  month: InvoiceOverageMonth;
  projectId: number;
}) {
  const monthLabel = formatMonthYear(month.period);
  return (
    <div className={month.included ? '' : 'opacity-60'}>
      <div className="flex justify-between items-center mb-2 flex-wrap gap-y-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-gray-900">
            {monthLabel === DATE_PLACEHOLDER ? month.period : monthLabel}
          </span>
          {month.isPriorMonth && (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-[10.5px] font-medium bg-indigo-100 text-indigo-800">
              From previous month
            </span>
          )}
          {month.hasZeroRate && (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-[10.5px] font-medium bg-rose-200 text-rose-900">
              Zero-rate row
            </span>
          )}
          <OverageApprovalChip approval={month.approval} hours={month.hours} />
          {!month.included && month.notIncludedReason && (
            <>
              <span className="inline-flex items-center px-2 py-0.5 rounded text-[10.5px] font-medium bg-gray-200 text-gray-800">
                {NOT_INCLUDED_LABEL[month.notIncludedReason]}
              </span>
              <Link
                to={`/projects/${projectId}#overage-approvals`}
                className="text-[12px] text-blue-600 hover:text-blue-800 hover:underline"
              >
                Approve →
              </Link>
            </>
          )}
        </div>
        <span className="text-sm text-gray-700">
          {month.hours.toFixed(1)} h / {formatCurrency(month.billing)}
        </span>
      </div>
      <OverageRoleTable byRole={month.byRole} />
    </div>
  );
}

function OverageSection({
  overage,
  projectId,
}: {
  overage: NonNullable<InvoiceBreakdown['byProject'][number]['overage']>;
  projectId: number;
}) {
  // MKS-552 — absence, never falsiness: an invoice issued before this ticket
  // (and any invoice computed with `mks_552_overage_by_month` off) carries no
  // `byMonth` at all and must render exactly as it did then.
  const byMonth = overage.byMonth;
  return (
    <div className="border-t border-gray-100 bg-amber-50/40 px-6 py-3">
      <div className="flex justify-between items-center mb-2">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-900">Overage (over package)</span>
          <span className="inline-flex items-center px-2 py-0.5 rounded text-[10.5px] font-medium bg-amber-200 text-amber-900">
            FIFO
          </span>
          {overage.hasZeroRate && (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-[10.5px] font-medium bg-rose-200 text-rose-900">
              Zero-rate row
            </span>
          )}
          {!byMonth && (
            <OverageApprovalChip approval={overage.approval} hours={overage.totalHours} />
          )}
        </div>
        <span className="text-sm text-gray-700">
          {overage.totalHours.toFixed(1)} h / {formatCurrency(overage.totalBilling)}
        </span>
      </div>
      {byMonth ? (
        <div className="space-y-4">
          {byMonth.map((month) => (
            <OverageMonthBlock key={month.period} month={month} projectId={projectId} />
          ))}
        </div>
      ) : (
        <OverageRoleTable byRole={overage.byRole} />
      )}
    </div>
  );
}

// QO0 — Billable Fixed Price row (kind='fixed_price').
// Renders a green header with the contract amount in its source currency, the
// ČNB FX metadata line (for non-CZK contracts), and the CZK billed amount.
// totalBilling (= CZK amount) feeds the footer total via the server-side sum.
function FixedPriceProjectRow({
  project,
}: {
  project: InvoiceBreakdown['byProject'][number];
}) {
  const contractAmountNum =
    project.contractAmount != null ? parseFloat(project.contractAmount) : 0;
  const currency = project.contractCurrency ?? '';
  const fxRate = project.fxRate;

  const formattedContractAmount = currency
    ? numberFormatter({
        style: 'currency',
        currency,
        maximumFractionDigits: 2,
      }, LEGACY_EN_NUMBER_LOCALE).format(contractAmountNum)
    : formatCurrency(contractAmountNum);

  return (
    <div className="border-b border-gray-100 last:border-b-0">
      <div className="px-6 py-3 bg-green-50/40">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-900">
              <Link to={`/projects/${project.projectId}`} className="hover:underline">
                {project.projectName}
              </Link>
            </span>
            <span className="inline-flex items-center px-2 py-0.5 rounded text-[10.5px] font-medium bg-green-100 text-green-800">
              Fixed Price
            </span>
          </div>
          <span className="text-sm text-gray-600">
            {formattedContractAmount} / {formatCurrency(project.totalBilling)}
          </span>
        </div>
      </div>
      <div className="px-6 py-3 grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
        <div>
          <div className="text-xs text-gray-500 uppercase tracking-wider mb-0.5">
            Contract amount
          </div>
          <div className="font-medium text-gray-900">{formattedContractAmount}</div>
          <div className="text-[11px] text-gray-500 mt-0.5">In contract currency</div>
        </div>
        <FxRateCell currency={currency} fxRate={fxRate} fxRateDate={project.fxRateDate} />
        <div className="text-right">
          <div className="text-xs text-gray-500 uppercase tracking-wider mb-0.5">
            Billed (CZK)
          </div>
          <div className="font-semibold text-gray-900 tabular-nums">
            {formatCurrency(project.totalBilling)}
          </div>
          <div className="text-[11px] text-gray-500 mt-0.5">Contributes to invoice total</div>
        </div>
      </div>
      <ExpenseLine project={project} />
      <DiscountLine project={project} />
    </div>
  );
}

// Phase 58 (D-02) — Fixed Price excluded row (legacy/historical, kind='fixed_price_excluded').
// Renders a muted gray header with the contract amount and an info line
// communicating that the project was billed manually outside this invoice batch.
// Contributes 0 to the footer total (server-side totalBilling is already 0).
function FixedPriceExcludedRow({
  project,
}: {
  project: InvoiceBreakdown['byProject'][number];
}) {
  const contractAmountNum = project.contractAmount != null ? parseFloat(project.contractAmount) : 0;
  const currency = project.contractCurrency ?? '';
  const formattedAmount = currency
    ? numberFormatter({
        style: 'currency',
        currency,
        maximumFractionDigits: 2,
      }, LEGACY_EN_NUMBER_LOCALE).format(contractAmountNum)
    : formatCurrency(contractAmountNum);

  return (
    <div className="border-b border-gray-100 last:border-b-0">
      <div className="px-6 py-3 bg-gray-50/50">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-900">
              <Link to={`/projects/${project.projectId}`} className="hover:underline">
                {project.projectName}
              </Link>
            </span>
            <span className="inline-flex items-center px-2 py-0.5 rounded text-[10.5px] font-medium bg-gray-200 text-gray-700">
              Fixed Price
            </span>
          </div>
          <span className="text-sm text-gray-500">
            {formattedAmount}
          </span>
        </div>
      </div>
      <div className="px-6 py-2 bg-gray-100/60">
        <p className="text-xs text-gray-500 italic">
          Billed manually — excluded from this invoice batch.
        </p>
      </div>
      <ExpenseLine project={project} />
    </div>
  );
}

// Phase 58 (PAY-TM-03) — Time & Material project row.
// Renders an orange header with per-role breakdown table, mirroring OverageSection.
// Zero-rate signals (hasZeroRate + row !hasRate) copy the rose badge pattern
// verbatim from OverageSection (InvoiceBreakdownTable.tsx:204-208, 241-245).
function TimeAndMaterialProjectRow({
  project,
}: {
  project: InvoiceBreakdown['byProject'][number];
}) {
  const byRole = project.byRole ?? [];

  return (
    <div className="border-b border-orange-100 last:border-b-0">
      <div className="px-6 py-3 bg-orange-50/40">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-900">
              <Link to={`/projects/${project.projectId}`} className="hover:underline">
                {project.projectName}
              </Link>
            </span>
            <span className="inline-flex items-center px-2 py-0.5 rounded text-[10.5px] font-medium bg-orange-100 text-orange-800">
              Time & Material
            </span>
            {project.hasZeroRate && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-[10.5px] font-medium bg-rose-200 text-rose-900">
                Zero-rate row
              </span>
            )}
          </div>
          <span className="text-sm text-gray-600">
            {project.totalHours.toFixed(1)}h / {formatCurrency(project.totalBilling)}
          </span>
        </div>
      </div>
      <SourceCurrencyMetadata project={project} />
      <table className="min-w-full">
        <thead>
          <tr className="text-xs text-gray-500 uppercase">
            <th className="px-6 py-2 text-left tracking-wider">Role</th>
            <th className="px-6 py-2 text-right tracking-wider">Hours</th>
            <th className="px-6 py-2 text-right tracking-wider">Rate</th>
            <th className="px-6 py-2 text-right tracking-wider">Total</th>
          </tr>
        </thead>
        <tbody>
          {byRole.map((row) => (
            <tr key={`${row.roleId ?? 'none'}`} className="border-t border-orange-100/60">
              <td className="px-6 py-1.5 text-sm text-gray-900">
                {row.roleName ?? <span className="italic text-gray-500">&mdash;</span>}
                {!row.hasRate && (
                  <span className="ml-2 inline-flex items-center rounded bg-rose-100 px-1.5 py-0.5 text-[10px] font-semibold text-rose-800">
                    Rate not set
                  </span>
                )}
              </td>
              <td className="px-6 py-1.5 text-sm text-gray-700 text-right tabular-nums">
                {row.hours.toFixed(1)}
              </td>
              <td className="px-6 py-1.5 text-sm text-gray-700 text-right tabular-nums">
                {row.hasRate ? formatCurrency(row.hourlyRate) : <span className="text-gray-400">&mdash;</span>}
              </td>
              <td className="px-6 py-1.5 text-sm text-gray-900 text-right font-medium tabular-nums">
                {formatCurrency(row.billing)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <ExpenseLine project={project} />
      <DiscountLine project={project} />
    </div>
  );
}
